import './Bingo.scss';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../../../js/api';
import { useContext, useEffect, useState } from 'react';
import Modal from '../../../../components/Modal/Modal';
import Board from './Board';
import { UserContext } from '../../../../js/UserContext';
import Button from '../../../../components/Button/Button';
import { hasRole } from '../../../../js/utils';
import useDialog from '../../../../hooks/useDialog';

export default function Bingo() {

    const s_params = useSearchParams();
    const { roomNumber } = useParams();
    const navigate = useNavigate();
    const [room, setRoom] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const { user, loading } = useContext(UserContext);
    const { confirm, alert, prompt, DialogUI } = useDialog();

    const login_spy = s_params[0].get('login');


    const start = async () => {
        let res = await api.get(`/rooms/${roomNumber}`, { silent: true });

        if (!res || res.error) {
            const confirmJoin = await confirm('No estás en esta sala. ¿Quieres unirte?');
            if (!confirmJoin) {
                navigate('/misc/minigames/bingo', { replace: true });
                return;
            }

            const password = await prompt('Contraseña de la sala (déjalo vacío si es pública):', '') || '';

            const joinRes = await api.post(`/users/${user.login}/rooms/${roomNumber}`, {
                password,
            }, { silent: true });

            if (joinRes && joinRes.error) {
                await alert(joinRes.error);
                navigate('/misc/minigames/bingo', { replace: true });
                return;
            }

            res = await api.get(`/rooms/${roomNumber}`, { silent: true });
            if (!res || res.error) {
                await alert(res?.error || 'Error al cargar la sala tras unirte.');
                navigate('/misc/minigames/bingo', { replace: true });
                return;
            }
        }

        if (!res.id || !res.users) {
            await alert('Datos de sala inválidos.');
            navigate('/misc/minigames/bingo', { replace: true });
            return;
        }

        if (res.admin && user?.login) {
            setIsAdmin(res.admin === user.login || hasRole(user, 'root'));
        }
        return res;
    }

    useEffect(() => {
        if (!roomNumber) {
            navigate('/misc/minigames/bingo', { replace: true });
            return;
        }
        start().then((res) => {
            if (res) {
                setRoom(res);
            } else {
                console.error('No room data returned');
            }
        }).catch((error) => {
            console.error('Error starting Bingo:', error);
            alert('An error occurred while starting the Bingo game.');
            navigate('/misc/minigames/bingo', { replace: true });
        });
    }, []);

    if (!room) {
        return <p>Loading room data...</p>;
    }
    if (loading) {
        return <p>Loading user data...</p>;
    }

    const buttons = {
        room_details:
            room ? (
                <Modal text="Room Details">
                    <p>Admin: {room.admin}</p>
                    <br />
                    <p>Private: {room.is_private ? 'Yes' : 'No'}</p>
                    <br />
                    {room.password && <p>Password: {room.password}</p>}
                </Modal>
            ) : (
                <p>Loading room data...</p>
            ),
        delete_room:
            isAdmin ? (
                <Button text="Delete Room" class_="red white-text" onClick={async () => {
                    const ok = await confirm('¿Seguro que quieres eliminar esta sala? Esta acción no se puede deshacer.');
                    if (!ok) return;
                    const r = await api.delete(`/rooms/${room.id}`);
                    if (r && r.error) {
                        await alert(r.error);
                        return;
                    }
                    navigate('/misc/minigames/bingo', { replace: true });
                }} />
            ) : null,
        room_stats:
            <Modal text="Room Stats" fn={async () => {
                const results = await Promise.all(
                    room.users.map(async (u) => {
                        const ch = await api.get(`/users/${u}/rooms/${room.id}/challenges`, { silent: true });
                        const challenges = ch && !ch.error && Array.isArray(ch) ? ch : [];
                        return { user: u, challenges };
                    })
                );

                const sorted = [...results].sort((a, b) => {
                    const aCompleted = a.challenges.filter(c => c.completed).length;
                    const bCompleted = b.challenges.filter(c => c.completed).length;
                    return bCompleted - aCompleted;
                });

                return (
                    <div className='room-stats'>
                        <h3>Progreso del grupo</h3>
                        <br />
                        <table className='stats-table'>
                            <thead>
                                <tr>
                                    <th>Jugador</th>
                                    <th>Completados</th>
                                    <th>Total</th>
                                    <th>Progreso</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map(({ user: u, challenges }) => {
                                    const completed = challenges.filter(c => c.completed).length;
                                    const total = challenges.length;
                                    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
                                    const isSelf = u === user.login;
                                    const displayName = u.charAt(0).toUpperCase() + u.slice(1);
                                    return (
                                        <tr key={u}>
                                            <td>
                                                {isSelf
                                                    ? <span className='stats-self'>{displayName} (tú)</span>
                                                    : <a href={`/misc/minigames/bingo/rooms/${room.id}?login=${u}`}>{displayName}</a>
                                                }
                                            </td>
                                            <td>{completed}</td>
                                            <td>{total}</td>
                                            <td>
                                                <div className='progress-bar-container'>
                                                    <div
                                                        className='progress-bar-fill'
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                    <span className='progress-bar-label'>{pct}%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                );
            }} />,
    }

    const user_spy = s_params[0].get('login');

    const info_container = (
        <div className='info-container flex gap flex-center flex-wrap border border-radius margin'>
            <p className='info'>Viewing room for user: <b>{user_spy}</b></p>
            <Button text="Back to My Room" onClick={() => {
                navigate(`/misc/minigames/bingo/rooms/${room.id}`, { replace: true });
                window.location.reload();
            }} />
        </div>
    )

    return (
        <div id='bingo'>
            {DialogUI}
            <div className='centered-container'>
                <h3 className='title'>{`Room: ${room.name} (code: ${room.id})`}</h3>
                {(user_spy && user_spy !== user.login) ? info_container : null}
                <div className='flex gap flex-wrap flex-center'>
                    {buttons.room_details}
                    {buttons.stats}
                    {buttons.delete_room}
                    {buttons.room_stats}
                </div>

                {room ? (
                    <Board rows={4} columns={3} room={room} />
                ) : (
                    <p>Loading board...</p>
                )}

            </div>
        </div>
    );
}